// aesCrypto.js
import crypto from "crypto";

/**
 * Generate a random 32-byte key for AES-256-GCM and return it as base64 string.
 * Use this once and store securely, then share with Python.
 */
export function generateKey() {
  const key = crypto.randomBytes(32); // 256 bits
  return key.toString("base64");
}

function getKeyBytes(keyB64) {
  return Buffer.from(keyB64, "base64");
}

/**
 * Encrypt a string using AES-256-GCM.
 *
 * Format: base64( iv || tag || ciphertext )
 */
export function encryptString(plaintext, keyB64, aad = null) {
  const key = getKeyBytes(keyB64);
  const iv = crypto.randomBytes(12); // 96-bit IV

  const cipher = crypto.createCipheriv("aes-256-gcm", key, iv);

  if (aad) {
    cipher.setAAD(Buffer.from(aad));
  }

  const ciphertext = Buffer.concat([
    cipher.update(plaintext, "utf8"),
    cipher.final(),
  ]);

  const tag = cipher.getAuthTag();

  const blob = Buffer.concat([iv, tag, ciphertext]);
  return blob.toString("base64");
}

/**
 * Decrypt a base64 string produced by encryptString.
 */
export function decryptString(ciphertextB64, keyB64, aad = null) {
  const key = getKeyBytes(keyB64);
  const blob = Buffer.from(ciphertextB64, "base64");

  const iv = blob.slice(0, 12);
  const tag = blob.slice(12, 28);
  const ciphertext = blob.slice(28);

  const decipher = crypto.createDecipheriv("aes-256-gcm", key, iv);

  if (aad) {
    decipher.setAAD(Buffer.from(aad));
  }

  decipher.setAuthTag(tag);

  const plaintext = Buffer.concat([
    decipher.update(ciphertext),
    decipher.final(),
  ]);

  return plaintext.toString("utf8");
}

// // Example usage when run directly with Node (ESM):
// if (import.meta.url === `file://${process.argv[1]}`) {
//   const keyB64 = generateKey();
//   console.log("Key (base64):", keyB64);

//   const secret = "My super secret string";

//   const token = encryptString(secret, keyB64);
//   console.log("Encrypted:", token);

//   const plain = decryptString(token, keyB64);
//   console.log("Decrypted:", plain);
// }